import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, formatDate } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { ButtonComponent } from '@app/lib/components/button/button.component';
import { TAny, TIdName, TKeyValue } from '@app/lib/models/common.type';
import { IResponse } from '@app/lib/models/response.model';
import { IBreadcrumb } from '@lib/models/breadcrumb.model';
import { CaseDocumentsComponent } from '../case-documents/case-documents.component';
import { ICourtSessionPanel, ICourtSessionPanelParams, IPagination, ISearchRequestParams } from '../models/case-assignment.model';
import { ITable, ITableColumn } from '../models/common.model';
import { IHearCaseSearchFormData, IHearingSaveParams, IHearingSchedule, IHearingScheduleCourtRoom, IHearingScheduleParams, IJusticePanel } from '../models/hearing-schedule';
import { HearingScheduleService } from '../services/hearing-schedule.service';
import { HearingCaseDetailsComponent } from './hearing-case-details/hearing-case-details.component';
import { AssignedTaskService } from '../services/assigned-task.service';
import { ETaskScreenName, IAssignedTask, IAssignedTaskEvent } from '../models/assigned-task.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ECaseCategory } from '../models/constant';
import { DialogService } from '@app/lib/services/dialog.service';
import { ILoginData } from '@app/lib/models/login.model';
import { DialogMessageComponent } from '../dialog-message/dialog-message.component';
import { MatTabsModule } from '@angular/material/tabs';
import { DatePipe } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DashboardComponent } from '../dashboard.component';
import { ConfirmationDialogComponent } from '@app/confirmation-dialog/confirmation-dialog.component';
import { MatTooltip } from '@angular/material/tooltip';

export interface ILocalHearingSchedule extends IHearingSchedule {
  originalHearingDate?: number | null;
  originalSessionId?: string | number | null;
  originalSessionName?: string | null;
  originalPanelId?: string | null;
  originalPanelName?: string | null;
  originalCourtUnitTypeId?: number | null;
  originalCourtUnitTypeName?: string | null;
}

@Component({
  selector: 'app-hearing-schedule',
  standalone: true,
  providers: [DatePipe],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    BreadcrumbComponent,
    MatDatepickerModule,
    ScrollingModule,
    MatProgressBarModule,
    CaseDocumentsComponent,
    ReactiveFormsModule,
    MatDialogModule,
    MatTableModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTooltip
  ],
  templateUrl: './hearing-schedule.component.html',
  styleUrl: './hearing-schedule.component.scss'
})
export class HearingScheduleComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) matSortRef!: MatSort;

  public caseCategoryEnum = ECaseCategory;

  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([]);

  public hearingPhases: string[] = [
    'Motion - Hearing Date To Be Fixed',
    'Motion - Hearing Date Fixed',
    'Hearing Date To Be Fixed',
    'Hearing Date Fixed'
  ];
  public caseCategories: TKeyValue[] = [
    { key: this.caseCategoryEnum.FI, value: 'First Instance' },
    { key: this.caseCategoryEnum.A, value: 'Appeal' },
  ];

  public searchFormData: WritableSignal<IHearCaseSearchFormData | null> = signal(null);
  sessionList: TIdName[] = []; // Or interface if it's an object
  panelList: ICourtSessionPanel[] = [];
  courtRoom: IHearingScheduleCourtRoom[] = []
  isSessionLoading: boolean = false;
  isPanelLoading: boolean = false;
  isCourtRoomLoading: boolean = false;
  ispanel: boolean = false;
  isOriginalPanelLoading: boolean = false;
  isChangedPanelLoading: boolean = false;
  columnsToDisplay = ['justiceId', 'justiceName'];
  originalPanel: IJusticePanel[] = [];
  changedPanel: IJusticePanel[] = [];
  isLoading: boolean = false;
  isAppeal: boolean = false;
  isSupremeFlag: boolean = false;
  isCOAflag: boolean = false;
  userComments: string = '';
  selectedRowForCancel: any = null;
  public searchForm: FormGroup = new FormGroup({
    hearingDate: new FormControl(''),
    hearingPhaseName: new FormControl('', [Validators.required]),
    caseCategory: new FormControl(''),
  });

  public hearingScheduleTbl: WritableSignal<ITable> = signal({
    columns: []
  });
  public displayedColumns: WritableSignal<string[]> = signal([]);

  today: Date = new Date(); // This sets min date to today

  public hearingMinDte: WritableSignal<Date> = signal(new Date());

  public dataSource: MatTableDataSource<ILocalHearingSchedule> = new MatTableDataSource();

  public task: WritableSignal<IAssignedTask | null> = signal(null);
  public caseNumber: WritableSignal<string> = signal('');
  public selectedComment: WritableSignal<string> = signal('');

  private get _gridColumns(): ITableColumn[] {
    return [
      {
        dataIndex: 'caseNumber',
        title: this.isSupremeFlag ? 'Appeal Number' : 'Case Number',
      },
      {
        dataIndex: 'hearingPhaseName',
        title: 'Hearing Phase',
      },
      {
        dataIndex: 'hearingDate',
        title: 'Hearing Date',
      },
      {
        dataIndex: 'courtUnitTypeName',
        title: 'Court',
        columnDisplayFn: (prop: ECaseCategory) => {
          const courtId = this.getCourtNameData()?.courtId;
          const isAllowedCategory = (prop === this.caseCategoryEnum.FI || prop === this.caseCategoryEnum.A);
          const isCourtRestricted = (courtId === 1 || courtId === 2);
          return isAllowedCategory && !isCourtRestricted;
        }
      },
      {
        dataIndex: 'sessionName',
        title: 'Session Name',
        columnDisplayFn: (prop: ECaseCategory) => (prop === this.caseCategoryEnum.A)
      },
      {
        dataIndex: 'panelName',
        title: 'Panel Name',
        columnDisplayFn: (prop: ECaseCategory) => (prop === this.caseCategoryEnum.A)
      },
      {
        dataIndex: 'courtRoom',
        title: 'Court Room',
        // ✅ Show only when courtId is 1 or 2
        columnDisplayFn: () => {
          const courtId = this.getCourtNameData()?.courtId;
          return courtId === 1 || courtId === 2;
        },
      },
      {
        dataIndex: 'actions',
        title: 'Actions',
        styleClass: '!w-44',
        sortable: false,
        headingStyleClass: '!text-end !w-44'
      }
    ];
  }
  constructor(
    private service: HearingScheduleService,
    private assignTaskService: AssignedTaskService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private dashboard: DashboardComponent
  ) {
    const courtId = this.getCourtNameData()?.courtId;
    this.isSupremeFlag = courtId === 1;
    this.isCOAflag = courtId === 2;
    this.assignTaskService.assignTask$
      .pipe(
        takeUntilDestroyed(),
        filter(event => event.name === ETaskScreenName.HEARINGSCHEDULE)
      )
      .subscribe({
        next: (event: IAssignedTaskEvent) => {
          this.caseNumber.set('');
          this.selectedComment.set('');
          this.cdr.detectChanges();
          this.task.set(event.task);
          let formData: IHearCaseSearchFormData;
          const courtId = this.getCourtNameData()?.courtId;
          if (courtId === 1 || courtId === 2) {
            formData = {
              hearingDate: this.task()?.hearingDate || '',
              hearingPhaseName: '',
              caseNumber: this.task()?.courtCaseNumber,
              triggeredTaskId: this.task()?.tasksId,
              taskTypeId: this.task()?.typeId,
              cphId: this.task()?.cphId,
              divisionId: this.task()?.divisionId
            };
          } else {
            formData = {
              caseCategory: this.task()?.caseCategory as ECaseCategory,
              hearingDate: this.task()?.hearingDate || '',
              hearingPhaseName: '',
              caseNumber: this.task()?.courtCaseNumber,
              triggeredTaskId: this.task()?.tasksId,
              taskTypeId: this.task()?.typeId,
              cphId: this.task()?.cphId,
              divisionId: this.task()?.divisionId
            };
          }
          this.getHearingCaseData(formData);
        }
      });
  }
  ngOnInit(): void {
    this.caseNumber.set('');
    this.selectedComment.set('');
    this.originalPanel = [];
    this.changedPanel = [];
    this.setCaseCategoryValidator();
    const courtId = this.getCourtNameData()?.courtId;
    if (courtId === 1 || courtId === 2) {
      if (courtId === 1) {
        this.breadcrumbs.set([{ label: 'Appeal' },
        { label: 'Hearing Schedule' },])
        this.isSupremeFlag = true;
      } else {
        this.breadcrumbs.set([{ label: 'Appeal' },
        { label: 'Hearing Schedule' },])
        this.isCOAflag = true
      }

    } else {
      this.breadcrumbs.set([{ label: 'Case' },
      { label: 'Hearing Schedule' },])
    };


  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.matSortRef;

    if (this.matSortRef) {
      this.dataSource.sort = this.matSortRef;
      this.matSortRef.sortChange.subscribe(event => {
        console.log('Sort changed:', event);
      });
    } else {
      console.warn('MatSort is not available');
    }


    this.dataSource.sortingDataAccessor = (item: ILocalHearingSchedule, property: string): string | number => {
      switch (property) {
        case 'caseNumber':
        case 'appealNumber':
          return item.caseNumber || '';
        case 'hearingPhaseName':
          return item.hearingPhaseName || '';
        case 'hearingDate':
          return item.hearingDate ? new Date(item.hearingDate).getTime() : 0;
        case 'courtUnitTypeName':
          return item.courtUnitTypeName || '';
        case 'sessionName':
          return item.sessionName || '';
        case 'panelName':
          return item.panelName || '';
        case 'courtRoom':
          return item.courtUnitTypeName || ''; // or a field like `courtRoomName`
        default:
          const value = item[property as keyof ILocalHearingSchedule];
          return typeof value === 'string' || typeof value === 'number' ? value : '';
      }
    };
  }
  private setCaseCategoryValidator(): void {
    const courtId = this.getCourtNameData()?.courtId;
    const caseCategoryCtrl = this.searchForm.get('caseCategory');

    if (courtId !== 1 && courtId !== 2) {
      caseCategoryCtrl?.setValidators([Validators.required]);
    } else {

      caseCategoryCtrl?.clearValidators();
    }

    caseCategoryCtrl?.updateValueAndValidity();
  }

  public searchHandler() {
    this.caseNumber.set('');
    this.selectedComment.set('')
    this.originalPanel = [];
    this.changedPanel = [];
    this.dataSource.data = [];
    this.task.set(null);
    this.searchFormData.set(null);
    if (this.searchForm.valid) {
      const formData = this.searchForm.value;
      this.getHearingCaseData(formData);
    }
  }

  public caseNumberClickHandler(row: ILocalHearingSchedule) {
    if (!(this.getCourtNameData()?.courtId === 1 || this.getCourtNameData()?.courtId === 2)) {
      this.caseNumber.set(row.caseNumber);
      this.selectedComment.set(row.comments ?? '')
      if (row.caseCategory === 'A') { this.isAppeal = true; console.log('isappealtrue') }
    }
    else {
      this.ispanel = true;
      this.caseNumber.set(row.caseNumber);
      this.selectedComment.set(row.comments ?? '')
      this.originalPanelJustice(row);

    }
  }
  originalPanelJustice(row: ILocalHearingSchedule,): void {
    if (row) {
      const reqParams: TAny = {
        id: row.uniqueIdProperty,
        caseId: row.caseId,
        caseNumber: row.caseNumber,
        hearingPhaseName: row.hearingPhaseName,
        hearingPhaseId: row.hearingPhaseId,
        hearingDate: formatDate(row.hearingDate, 'YYYY-MM-ddThh:mm:ss', 'en-US'),
        sessionId: row.sessionId,
        sessionName: row.sessionName,
        panelId: row.panelId,
        panelName: row.panelName,
        courtUnitTypeId: row.courtUnitTypeId,
        courtUnitTypeName: row.courtUnitTypeName,
        triggeredTaskId: row.triggeredTaskId,
        uniqueIdProperty: row.uniqueIdProperty,
        transType: '',
        cphId: row.cphId
      };

      this.isOriginalPanelLoading = true;
      this.service.fetchOriginalJusticePanel(reqParams).subscribe({
        next: (res: IResponse<IJusticePanel[]>) => {
          this.originalPanel = res.data || res.success || [];
          this.isOriginalPanelLoading = false;
        },
        error: () => {
          this.isOriginalPanelLoading = false;
        }
      });
    }
    this.changedPanelJustice(row);
  }
  changedPanelJustice(row: ILocalHearingSchedule,): void {
    if (row) {
      const reqParams: TAny = {
        id: row.uniqueIdProperty,
        caseId: row.caseId,
        caseNumber: row.caseNumber,
        hearingPhaseName: row.hearingPhaseName,
        hearingPhaseId: row.hearingPhaseId,
        hearingDate: formatDate(row.hearingDate, 'YYYY-MM-ddThh:mm:ss', 'en-US'),
        sessionId: row.sessionId,
        sessionName: row.sessionName,
        panelId: row.panelId,
        panelName: row.panelName,
        courtUnitTypeId: row.courtUnitTypeId,
        courtUnitTypeName: row.courtUnitTypeName,
        triggeredTaskId: row.triggeredTaskId,
        uniqueIdProperty: row.uniqueIdProperty,
        transType: '',
        cphId: row.cphId
      };

      this.isChangedPanelLoading = true;
      this.service.fetchChangedJusticePanel(reqParams).subscribe({
        next: (res: IResponse<IJusticePanel[]>) => {
          this.changedPanel = res.data || res.success || [];
          this.isChangedPanelLoading = false;
        },
        error: () => {
          this.isChangedPanelLoading = false;
        }
      });
    }
  }

  public resetHandler() {
    this.dataSource = new MatTableDataSource<ILocalHearingSchedule>();
    this.task.set(null);
    this.caseNumber.set('');
    this.selectedComment.set('')
    this.searchFormData.set(null);
    this.originalPanel = [];
    this.changedPanel = [];
  }
  public onSave(row: ILocalHearingSchedule) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Confirm Action',
        message: 'Do you want to Save?',
      },
      disableClose: true,
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (!(this.getCourtNameData()?.courtId === 1 || this.getCourtNameData()?.courtId === 2)) {
          this.updateRow(row);
        } else {
          this.updateRowForAppeal(row);
        }
      } else {
        console.log('Cancelled!');
      }
    });
  }
  public updateRow(row: ILocalHearingSchedule) {
    row.editedLoader = true;
    const reqBody: TAny = {
      id: row.uniqueIdProperty,
      caseCategory: row.caseCategory,
      caseId: row.caseId,
      caseNumber: row.caseNumber,
      courtUnitTypeId: row.courtUnitTypeId,
      courtUnitTypeName: row.courtUnitTypeName,
      cphId: row.cphId,
      hearingPhaseId: row.hearingPhaseId,
      hearingPhaseName: row.hearingPhaseName,
      panelActiveEndDateStr: row.panelActiveEndDateStr === null ? '' : row.panelActiveEndDateStr,
      panelActiveStartDateStr: row.panelActiveStartDateStr === null ? '' : row.panelActiveStartDateStr,
      panelId: row.panelId === null ? 0 : row.panelId,
      panelName: row.panelName,
      sessionId: row.sessionId === null ? 0 : row.sessionId,
      sessionName: row.sessionName,
      triggeredTaskId: this.task()?.tasksId || '',
      uniqueIdProperty: row.uniqueIdProperty,
      hearingDate: formatDate(row.editedCtrlVal, 'YYYY-MM-ddThh:mm:ss', 'en-US'),
    };
    if (row.caseCategory !== this.caseCategoryEnum.FI) {
      this.service.assignHearingDateForAppeal(reqBody).subscribe({
        next: (res: IResponse<any>) => {
          row.editedLoader = false;
          row.editable = false;
          if (res.success) {
            const isError = !res.message.includes('Saved Successfully');
            this.dialogService.openDialog1({
              data: {
                title: isError ? 'Error' : 'Message',
                message: res.message || (isError ? 'Something went wrong' : 'Record updated successfully'),
                icon: isError ? 'error' : 'done_outline', // ✅ Default icon if not provided
              }, disableClose: true,           // Prevents closing on outside click or ESC
              hasBackdrop: true,            // Shows backdrop to block interaction
              backdropClass: 'dialog-backdrop', // Optional: custom styling
            });
            if (res.message.includes('Saved Successfully')) {
              row.hearingDate = row.editedCtrlVal.getTime();
              row.canEdit = false;
              this.task.set(null);
              this.dashboard.refreshSidebarTasks();
            }
          }
        }
      });
    } else {
      this.service.assignHearingDateForCourt(reqBody).subscribe({
        next: (res: IResponse<any>) => {
          row.editedLoader = false;
          row.editable = false;
          if (res.success) {
            const isError = !res.message.includes('Saved Successfully');
            this.dialogService.openDialog1({
              data: {
                title: isError ? 'Error' : 'Message',
                message: res.message || (isError ? 'Something went wrong' : 'Record updated successfully'),
                icon: isError ? 'error' : 'done_outline', // ✅ Default icon if not provided
              }, disableClose: true,           // Prevents closing on outside click or ESC
              hasBackdrop: true,            // Shows backdrop to block interaction
              backdropClass: 'dialog-backdrop', // Optional: custom styling
            });
            if (res.message.includes('Saved Successfully')) {
              row.hearingDate = row.editedCtrlVal.getTime();
              row.canEdit = false;
              this.task.set(null)
            }
          }
        }
      });
    }
  }
  openCancelHearingDialog(row: any, dialog: HTMLDialogElement): void {
    this.selectedRowForCancel = row;
    this.userComments = ''; // Reset input field on open
    dialog.showModal();
  }

  onCancelHearingDate(dialog: HTMLDialogElement): void {
    if (!this.selectedRowForCancel) return;

    const row = this.selectedRowForCancel;
    const reason = this.userComments.trim();

    if (!reason) {
      alert('Please enter comments before saving.');
      return;
    }

    row.editedLoader = true;

    const payLoad = {
      "caseId": row.caseId,
      "cphId": row.cphId,
      "hearingDate": row.hearingDate,
      "courtId": this.getCourtNameData()?.courtId,
      "reason": reason
    }
    dialog.close(); // Close dialog immediately or keep open until response

    this.service.cancelHearingDate(payLoad).subscribe({
      next: (res: any) => {
        row.editedLoader = false;
        row.status = 'cancelled';
        this.selectedRowForCancel = null;
        this.userComments = '';

        this.dialogService.openDialog1({
          data: {
            title: 'Success',
            message: res.message || 'Record updated successfully',
            icon: 'done_outline', // ✅ Default icon if not provided
          }, disableClose: true,           // Prevents closing on outside click or ESC
          hasBackdrop: true,            // Shows backdrop to block interaction
          backdropClass: 'dialog-backdrop', // Optional: custom styling
        });
      },
      error: (err: any) => {
        row.editedLoader = false;
        console.error('Failed to cancel hearing date', err);
      }
    });
  }
  public updateRowForAppeal(row: ILocalHearingSchedule) {
    row.editedLoader = true;
    const reqBody: TAny = {
      id: row.uniqueIdProperty,
      caseId: row.caseId,
      caseNumber: row.caseNumber,
      courtUnitTypeId: row.courtUnitTypeId,
      courtUnitTypeName: row.courtUnitTypeName,
      cphId: row.cphId,
      hearingPhaseId: row.hearingPhaseId,
      hearingPhaseName: row.hearingPhaseName,
      panelId: row.panelId,
      panelName: row.panelName,
      sessionId: row.sessionId,
      sessionName: row.sessionName,
      triggeredTaskId: this.task()?.tasksId || '',
      uniqueIdProperty: row.uniqueIdProperty,
      hearingDate: formatDate(row.editedCtrlVal, 'YYYY-MM-ddThh:mm:ss', 'en-US'),
      transType: "U",
      activeStartDate: row.panelActiveStartDateStr,
      activeEndDate: row.panelActiveEndDateStr
    };
    this.service.assignHearingDateForAppeal(reqBody).subscribe({
      next: (res: IResponse<any>) => {
        row.editedLoader = false;
        row.editable = false;
        if (res.success) {
          const isError = !res.message.includes('Saved Successfully');
          this.dialogService.openDialog1({
            data: {
              title: isError ? 'Error' : 'Success',
              message: res.message || (isError ? 'Something went wrong' : 'Record updated successfully'),
              icon: isError ? 'error' : 'done_outline', // ✅ Default icon if not provided
            }, disableClose: true,           // Prevents closing on outside click or ESC
            hasBackdrop: true,            // Shows backdrop to block interaction
            backdropClass: 'dialog-backdrop', // Optional: custom styling
          });
          if (res.message.includes('Saved Successfully')) {
            row.hearingDate = row.editedCtrlVal.getTime();
            row.canEdit = false;
            this.task.set(null);
            this.dashboard.refreshSidebarTasks();
          }
        }
      }
    })
  }

  onSessionSelected(row: any, selectedId: number): void {
    const selectedSession = this.sessionList.find(s => s.id === selectedId);
    if (selectedSession) {
      row.sessionId = selectedSession.id;
      row.sessionName = selectedSession.name;

      const reqParams: ICourtSessionPanelParams = {
        page: 1,
        start: 0,
        limit: 25,
        property: 'id',
        direction: 'ASC',
        id: selectedSession.id
      };

      this.isPanelLoading = true;
      this.service.getPanels(reqParams).subscribe({
        next: (res: IResponse<ICourtSessionPanel[]>) => {
          this.panelList = res.data || res.success || [];
          this.isPanelLoading = false;
        },
        error: () => {
          this.isPanelLoading = false;
        }
      });
    }
  }

  onPanelSelected(row: any, selectedId: number): void {
    const selectedPanel = this.panelList.find(p => p.id === selectedId);
    if (selectedPanel) {
      row.panelId = selectedPanel.id;
      row.panelName = selectedPanel.name;
      row.panelActiveStartDateStr = formatDate(selectedPanel.activeStartDate, 'YYYY-MM-ddThh:mm:ss', 'en-US');
      row.panelActiveEndDateStr = formatDate(selectedPanel.activeEndDate, 'YYYY-MM-ddThh:mm:ss', 'en-US');
    }
  }
  onCourtRoomSelected(row: any, selectedId: number): void {
    const selectedRoom = this.courtRoom.find(p => p.courtUnitTypeId === selectedId);
    if (selectedRoom) {
      row.courtUnitTypeId = selectedRoom.courtUnitTypeId;
      row.courtUnitTypeName = selectedRoom.courtUnitTypeName;
    }
  }
  public historyClickHandler(row: ILocalHearingSchedule) {
    this.dialog.open(HearingCaseDetailsComponent, {
      panelClass: 'dialog-pdf-viewer',
      data: row, disableClose: true,           // Prevents closing on outside click or ESC
      hasBackdrop: true,            // Shows backdrop to block interaction
      backdropClass: 'dialog-backdrop', // Optional: custom styling
    })
  }

  private getHearingCaseData(formData: IHearCaseSearchFormData) {
    const courtId = this.getCourtNameData()?.courtId;
    const effectiveCategory = (courtId === 1 || courtId === 2) ? this.caseCategoryEnum.A : formData.caseCategory;

    this.hearingScheduleTbl.update(tbl => {
      tbl.columns = this._gridColumns.filter(col => {
        if (typeof col.columnDisplayFn === 'function') {
          return col.columnDisplayFn(effectiveCategory);
        }
        return true;
      });
      return tbl;
    });
    this.displayedColumns.set(this.hearingScheduleTbl().columns.map(col => col.dataIndex));

    this.searchFormData.set(formData);

    formData.hearingDate = formData.hearingDate ? encodeURIComponent(formatDate(formData.hearingDate, 'dd-MMM-yyyy', 'en-US')) : '';
    const queryParams: ISearchRequestParams = {
      page: 1,
      start: 0,
      limit: 25,
      property: 'caseId',
      direction: 'ASC',
    };
    this.isLoading = true;

    this.service.getHearingTailCourts(formData as IHearingScheduleParams, queryParams)
      .subscribe({
        next: (res: IResponse<IHearingSchedule[]>) => {
          // --- 1. Deduplicate by comparing all properties except auditMessage ---
          const seen = new Set<string>();
          const uniqueData = (res.data || []).filter(item => {
            // Destructure out auditMessage and create a deterministic key
            const { auditMessage, ...rest } = item as any;

            // Sort keys to ensure reliable string comparison
            const signature = Object.keys(rest)
              .sort()
              .map(key => `${key}:${rest[key]}`)
              .join('|');

            if (seen.has(signature)) {
              return false;
            }
            seen.add(signature);
            return true;
          });

          // --- 2. Map and set data source ---
          this.dataSource.data = uniqueData.map((item) => {
            item.editable = false;
            switch (this.searchFormData()?.caseCategory) {
              case this.caseCategoryEnum.FI:
                item.canEdit = item.courtUnitTypeId !== null && item.courtUnitTypeId !== undefined;
                break;
              case this.caseCategoryEnum.A: {
                const currentCourtId = this.getCourtNameData()?.courtId;
                if (currentCourtId === 1 || currentCourtId === 2) {
                  item.canEdit = true;
                } else {
                  const isSessionEmpty = item.sessionId === null || item.sessionId === undefined || String(item.sessionId).trim() === '' || !item.sessionName || item.sessionName.trim() === '';
                  const isPanelEmpty = item.panelId === null || item.panelId === undefined || String(item.panelId).trim() === '' || !item.panelName || item.panelName.trim() === '';
                  item.canEdit = !isSessionEmpty && !isPanelEmpty;
                }
                break;
              }
              default:
                item.canEdit = (this.getCourtNameData()?.courtId === 1 || this.getCourtNameData()?.courtId === 2);
                break;
            }

            item.editedCtrlVal = item.hearingDate ? new Date(item.hearingDate) : new Date();
            item.editedLoader = false;

            return item;
          });

          this.isLoading = false;
          this.cdr.detectChanges();
          setTimeout(() => {
            if (this.matSortRef) {
              this.dataSource.sort = this.matSortRef;
              console.log('✅ MatSort initialized after data load');
            } else {
              console.warn('❌ MatSort not available after search');
            }
          });
        },
        error: () => {
          this.dataSource.data = [];
          this.isLoading = false;
        }
      });
  }
  getCourtSessions() {
    this.isSessionLoading = true;
    const queryParams: IPagination = {
      limit: 100,
      page: 1,
      start: 0
    };
    this.service.getCourtSessions(queryParams).subscribe({
      next: (res: IResponse<TIdName[]>) => {
        this.sessionList = res.data || res.success || [];
        this.isSessionLoading = false;
      },
      error: () => {
        this.isSessionLoading = false;
      }
    });
  }


  getCourtNameData(): ILoginData | null {
    try {
      const userData = localStorage.getItem('userData') as string;
      return userData ? JSON.parse(userData) : null;
    } catch (err) {
      return null
    }
  }

  public isIstCourt(): boolean {
    const courtInfo = this.getCourtNameData();
    console.log('### courtName:', courtInfo?.courtName);
    if (courtInfo && courtInfo.courtName) {
      return courtInfo.courtName.toLowerCase().includes('investments and securities tribunal');//for IST court changes
    }
    return false;
  }
  onHearingDateChange(row: ILocalHearingSchedule) {
    const selectedDate = row.editedCtrlVal;
    console.log('Date changed:', selectedDate);

    const formattedDate = this.datePipe.transform(selectedDate, 'dd-MMM-yyyy');

    this.isCourtRoomLoading = true;
    const queryParams: TAny = {
      courtUnitTypeId: this.getCourtNameData()?.courtId,
      hearingDate: formattedDate
    };
    this.service.getCourtRoom(queryParams).subscribe({
      next: (res: IResponse<IHearingScheduleCourtRoom[]>) => {
        this.courtRoom = res.data || res.success || [];
        this.isCourtRoomLoading = false;
      },
      error: () => {
        this.isCourtRoomLoading = false;
      }
    });
  }
  onEditToggle(row: ILocalHearingSchedule): void {
    row.editable = !row.editable;

    // Optional: Reset values, fetch dropdowns, or log
    console.log(`Edit mode ${row.editable ? 'enabled' : 'disabled'} for case: ${row.caseNumber}`);

    if (row.editable) {
      // Store original values for dirty checking
      row.originalHearingDate = row.hearingDate ? new Date(row.hearingDate).getTime() : null;
      row.originalSessionId = row.sessionId || null;
      row.originalSessionName = row.sessionName || null;
      row.originalPanelId = row.panelId || null;
      row.originalPanelName = row.panelName || null;
      row.originalCourtUnitTypeId = row.courtUnitTypeId || null;
      row.originalCourtUnitTypeName = row.courtUnitTypeName || null;

      // Optional additional action on entering edit mode
      this.getCourtSessions(); // or any API call
      this.onSessionSelected(row, Number(row.sessionId));
      this.onHearingDateChange(row);
    }
  }

  public onCancel(row: ILocalHearingSchedule): void {
    row.editable = false;
    // Revert to original values
    if (row.originalHearingDate !== undefined) {
      row.hearingDate = row.originalHearingDate || '';
      row.editedCtrlVal = row.originalHearingDate ? new Date(row.originalHearingDate) : new Date();
    }
    if (row.originalSessionId !== undefined) {
      row.sessionId = row.originalSessionId || '';
    }
    if (row.originalSessionName !== undefined) {
      row.sessionName = row.originalSessionName || '';
    }
    if (row.originalPanelId !== undefined) {
      row.panelId = row.originalPanelId || '';
    }
    if (row.originalPanelName !== undefined) {
      row.panelName = row.originalPanelName || '';
    }
    if (row.originalCourtUnitTypeId !== undefined) {
      row.courtUnitTypeId = row.originalCourtUnitTypeId || 0;
    }
    if (row.originalCourtUnitTypeName !== undefined) {
      row.courtUnitTypeName = row.originalCourtUnitTypeName || '';
    }
  }


  public isSaveDisabled(row: ILocalHearingSchedule): boolean {
    const courtId = this.getCourtNameData()?.courtId;
    const isSupremeOrCOA = courtId === 1 || courtId === 2;

    // Check if required fields are filled
    if (!row.editedCtrlVal) {
      return true;
    }

    if (isSupremeOrCOA) {
      if (!row.sessionId || !row.panelId || !row.courtUnitTypeId) {
        return true;
      }
    }

    // Check if any changes were made (must be dirty to enable save)
    const originalTime = row.originalHearingDate;
    const currentTime = row.editedCtrlVal ? new Date(row.editedCtrlVal).getTime() : null;

    let dateChanged = false;
    if (currentTime && originalTime) {
      const curDate = new Date(currentTime);
      const origDate = new Date(originalTime);
      dateChanged = curDate.getFullYear() !== origDate.getFullYear() ||
        curDate.getMonth() !== origDate.getMonth() ||
        curDate.getDate() !== origDate.getDate();
    } else if (currentTime || originalTime) {
      dateChanged = true;
    }

    if (isSupremeOrCOA) {
      const sessionChanged = String(row.sessionId || '') !== String(row.originalSessionId || '');
      const panelChanged = String(row.panelId || '') !== String(row.originalPanelId || '');
      const courtRoomChanged = Number(row.courtUnitTypeId || 0) !== Number(row.originalCourtUnitTypeId || 0);
      return !(dateChanged || sessionChanged || panelChanged || courtRoomChanged);
    } else {
      return !dateChanged;
    }
  }

}
